import { PasswordForm } from './PasswordForm'

/**
 * The screen an account is held on until it chooses a password of its own.
 *
 * It is rendered instead of the console rather than in front of it, so there is nothing behind it to
 * navigate back to. The server refuses every other endpoint anyway - this is the courtesy that stops
 * somebody discovering that one 403 at a time.
 *
 * It exists because this deployment has no way to send an invitation. Somebody else typed the
 * password that got this account here, so it is a shared secret until the moment it is replaced.
 *
 * The form itself is shared with the Settings card, which anybody can open whenever they like. What
 * stays here is only what this situation needs and the other must not inherit: a full-bleed frame
 * with no way around it, and words that say why somebody is being stopped.
 *
 * One wrapper, not two children. `.sign-in` centres a single grid item, so an intro beside the form
 * would become a second row with its own width.
 */
export function ChangePasswordPage({
  reason = 'handed-over',
  onChanged
}: {
  /**
   * Which of the two situations this is.
   *
   * Said out loud because they are not the same thing and the wrong words are a small accusation:
   * somebody whose password has simply aged out has not been handed anything by anybody.
   */
  reason?: 'handed-over' | 'expired'
  onChanged: () => void
}) {
  const expired = reason === 'expired'

  return (
    <div className="sign-in">
      <div className="sign-in-panel">
        <h1>{expired ? 'Your password has expired' : 'Choose a password'}</h1>
        <p className="muted">
          {expired
            ? 'This organization asks for a new password from time to time, and yours has reached that age. You stay signed in once you have chosen one.'
            : 'This account was given a password by somebody else. Choose one only you know before going any further.'}
        </p>

        <PasswordForm submitLabel="Save and continue" savingLabel="Saving…" onChanged={onChanged} />
      </div>
    </div>
  )
}
